import { useQuery } from '@tanstack/react-query';
import { Dispatch } from 'redux';

import { getFactoringRequestsReceived, requestHandler } from '../../api';
import {
  CancelledInvoicesDisclaimer,
  FactoringRequestToApproveResponse,
  GetAvailableArrearTablesResponse,
  GetExecutivesAvailableForFactoringResponse,
  GetFactoringRequestsReceivedResponse,
} from './types';

export const useFactoringRequestsReceived = (
  dispatch: Dispatch,
  query: string,
) =>
  useQuery({
    queryKey: ['factoring', 'requests', query],
    queryFn: async () => {
      const response = await getFactoringRequestsReceived(dispatch, query);
      if (response) return response as GetFactoringRequestsReceivedResponse;
      return {
        data: [],
        pagination: {
          page: 1,
          pageSize: 10,
          total: 0,
          totalPages: 0,
        },
      };
    },
    staleTime: 1000 * 60 * 2,
    retry: 1,
  });

export const createFactoringRequest = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/factoring/request`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const checkFactoringRequestDeletion = async (
  dispatch: Dispatch,
  id: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/factoring/request/${id}/checkDelete`,
    dispatch,
  });
  if (response) return response.data;
};

export const deleteFactoringRequest = async (
  dispatch: Dispatch,
  id: number,
) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/factoring/request/${id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const updateStatusFactoringRequest = async (
  dispatch: Dispatch,
  id: number,
  data: any,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/factoring/request/${id}/updateStatus`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const editParamsOnReview = async (
  dispatch: Dispatch,
  id: number,
  data: any,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/factoring/request/${id}/inReview/editParams`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const addOwnCompanyConfirmationsToDocuments = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/users/factoring/documents/addOwnConfirmation`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const addExtraDataToDocument = async (
  dispatch: Dispatch,
  data: {
    documents: any[];
    attributes: string[];
  },
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/users/factoring/documents/addExtraData`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const activateNullFactoringRequest = async (
  dispatch: Dispatch,
  id: number,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/factoring/request/${id}/payments/activateNull`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getAvailableArrearTables = async (
  dispatch: Dispatch,
): Promise<GetAvailableArrearTablesResponse | undefined> => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/factoring/arrearTables`,
    dispatch,
  });
  if (response) return response.data;
};

export const getReplacementInvoices = async (
  dispatch: Dispatch,
  documentId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/factoring/replacementInvoices/${documentId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const replaceInvoice = async (
  dispatch: Dispatch,
  data: any,
  documentId: string,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/factoring/replaceInvoice/${documentId}`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getExecutivesAvailableForFactoring = async (
  dispatch: Dispatch,
  businessIdentifier: string,
): Promise<GetExecutivesAvailableForFactoringResponse | undefined> => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/factoring/executivesAvailable/${businessIdentifier}`,
    dispatch,
  });
  if (response) return response.data;
};

export const editExecutive = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/users/factoring/executive`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getLastActiveQuote = async (
  dispatch: Dispatch,
  businessIdentifier: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/factoring/lastActiveQuote/${businessIdentifier}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getRecieverFactoringRequest = async (
  dispatch: Dispatch,
  data: { requestId: string },
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/factoring/reciever/requests/${data.requestId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getRecieverFactoringRequestOverview = (
  dispatch: Dispatch,
  data: { requestId: string },
) =>
  useQuery({
    queryKey: ['factoring', 'request', data.requestId, 'overview'],
    queryFn: async () => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/users/factoring/reciever/requests/${data.requestId}/overview`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    enabled: !!data.requestId,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

export const getFactoringRequestsReceivedToApprove = (
  dispatch: Dispatch,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: ['factoring', 'requestsToApprove'],
    queryFn: async () => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/users/factoring/reciever/requestsToApprove`,
        dispatch,
      });
      if (response) return response.data as FactoringRequestToApproveResponse;
      return { data: [] };
    },
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

export const getCancelledInvoicesDisclaimer = (
  dispatch: Dispatch,
  businessIdentifier: string,
) =>
  useQuery({
    queryKey: ['factoring', 'cancelledInvoices', businessIdentifier],
    queryFn: async (): Promise<CancelledInvoicesDisclaimer | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/factoring/cancelledInvoicesDisclaimer/${businessIdentifier}`,
        dispatch,
      });
      if (response) return response.data;
      return;
    },
    retry: false,
  });
